import { Badge } from '@heidi/design-system/badge';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { TypographyCaption, TypographyH4 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { MessageCirclePlus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { VoiceEvidenceCard } from '../components/VoiceEvidenceCard';
import { InfoBlockView } from './InfoCardBlocks';
import { voiceWorkspaceMessages as messages } from './workspace.messages';
import type { EvidenceInfoCard, GenericInfoCard, InfoCard } from './workspace.types';

/** House entrance for feed items — compositor props only, reduced-motion safe. */
const CARD_ENTER =
  'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-300 motion-safe:ease-out-quart motion-reduce:animate-none';

export type InfoCardPresentation = 'board' | 'inline';

function FollowUpRow({
  followUps,
  followUpsEnabled,
  onFollowUp,
  className,
}: {
  followUps: string[];
  followUpsEnabled: boolean;
  onFollowUp: (text: string) => void;
  className?: string;
}) {
  const intl = useIntl();
  if (followUps.length === 0) return null;
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <TypographyCaption className="me-1 text-text-tertiary">
        {intl.formatMessage(messages.goDeeper)}
      </TypographyCaption>
      {followUps.map((followUp) => (
        <button
          key={followUp}
          type="button"
          disabled={!followUpsEnabled}
          onClick={() => onFollowUp(followUp)}
          className={cn(
            'flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5',
            'text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary',
            'disabled:pointer-events-none disabled:opacity-50',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus'
          )}
          data-testid="workspace-info-followup"
        >
          <MessageCirclePlus className="size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0 truncate">{followUp}</span>
        </button>
      ))}
    </div>
  );
}

function CardChrome({
  ariaLabel,
  tone,
  ts,
  title,
  subtitle,
  children,
  followUps,
  followUpsEnabled,
  onFollowUp,
  presentation,
}: {
  ariaLabel: string;
  tone?: GenericInfoCard['tone'];
  ts: number;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  followUps?: string[];
  followUpsEnabled: boolean;
  onFollowUp: (text: string) => void;
  presentation: InfoCardPresentation;
}) {
  const intl = useIntl();
  const inline = presentation === 'inline';

  return (
    <article
      className={cn(
        'flex flex-col overflow-hidden',
        CARD_ENTER,
        inline ? 'gap-2' : 'rounded-2xl border border-border bg-surface shadow-sm'
      )}
      aria-label={ariaLabel}
      data-testid="workspace-info-card"
      data-presentation={presentation}
    >
      {inline ? (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            {tone === 'attention' ? (
              <Badge color="orange" size="sm">
                {intl.formatMessage(messages.attentionBadge)}
              </Badge>
            ) : null}
            <TypographyCaption className="ms-auto tabular-nums text-text-tertiary">
              {intl.formatTime(ts)}
            </TypographyCaption>
          </div>
          {title ? <TypographyH4 className="text-balance">{title}</TypographyH4> : null}
          {subtitle ? (
            <TypographyCaption className="text-pretty text-text-secondary">
              {subtitle}
            </TypographyCaption>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-0.5 border-b border-border/60 px-5 py-4">
          <div className="flex items-center gap-1.5">
            <HeidiLogoMark className="size-3 text-text-secondary" />
            <TypographyCaption className="text-text-tertiary">
              {intl.formatMessage(messages.presentedByHeidi)}
            </TypographyCaption>
            <span className="flex-1" />
            {tone === 'attention' ? (
              <Badge color="orange" size="sm">
                {intl.formatMessage(messages.attentionBadge)}
              </Badge>
            ) : null}
            <TypographyCaption className="tabular-nums text-text-tertiary">
              {intl.formatTime(ts)}
            </TypographyCaption>
          </div>
          {title ? <TypographyH4 className="text-balance">{title}</TypographyH4> : null}
          {subtitle ? (
            <TypographyCaption className="text-pretty text-text-secondary">
              {subtitle}
            </TypographyCaption>
          ) : null}
        </div>
      )}

      <div className={cn('flex flex-col gap-4', inline ? undefined : 'px-5 py-4')}>
        {children}
      </div>

      {followUps && followUps.length > 0 ? (
        <FollowUpRow
          followUps={followUps}
          followUpsEnabled={followUpsEnabled}
          onFollowUp={onFollowUp}
          className={inline ? undefined : 'border-t border-border/60 px-5 py-3'}
        />
      ) : null}
    </article>
  );
}

function GenericInfoCardView({
  card,
  followUpsEnabled,
  onFollowUp,
  presentation,
}: {
  card: GenericInfoCard;
  followUpsEnabled: boolean;
  onFollowUp: (text: string) => void;
  presentation: InfoCardPresentation;
}) {
  return (
    <CardChrome
      ariaLabel={card.title}
      tone={card.tone}
      ts={card.ts}
      title={card.title}
      subtitle={card.subtitle}
      followUps={card.followUps}
      followUpsEnabled={followUpsEnabled}
      onFollowUp={onFollowUp}
      presentation={presentation}
    >
      {card.blocks.map((block, index) => (
        <InfoBlockView key={index} block={block} />
      ))}
    </CardChrome>
  );
}

function EvidenceInfoCardView({
  card,
  followUpsEnabled,
  onFollowUp,
  presentation,
}: {
  card: EvidenceInfoCard;
  followUpsEnabled: boolean;
  onFollowUp: (text: string) => void;
  presentation: InfoCardPresentation;
}) {
  const intl = useIntl();
  const evidence = (
    <VoiceEvidenceCard
      text={card.text}
      sources={card.sources}
      className="w-full max-w-none"
    />
  );

  // Inline: evidence already has its own Key Points surface — no outer card chrome.
  if (presentation === 'inline') {
    return (
      <div
        className={cn('flex flex-col gap-2', CARD_ENTER)}
        data-testid="workspace-info-card"
        data-presentation="inline"
        aria-label={intl.formatMessage(messages.evidenceCardAriaLabel)}
      >
        {evidence}
        {card.followUps && card.followUps.length > 0 ? (
          <FollowUpRow
            followUps={card.followUps}
            followUpsEnabled={followUpsEnabled}
            onFollowUp={onFollowUp}
          />
        ) : null}
      </div>
    );
  }

  return (
    <CardChrome
      ariaLabel={intl.formatMessage(messages.evidenceCardAriaLabel)}
      ts={card.ts}
      followUps={card.followUps}
      followUpsEnabled={followUpsEnabled}
      onFollowUp={onFollowUp}
      presentation={presentation}
    >
      {evidence}
    </CardChrome>
  );
}

export function InfoCardView({
  card,
  followUpsEnabled,
  onFollowUp,
  presentation = 'board',
}: {
  card: InfoCard;
  followUpsEnabled: boolean;
  onFollowUp: (text: string) => void;
  presentation?: InfoCardPresentation;
}) {
  switch (card.kind) {
    case 'evidence':
      return (
        <EvidenceInfoCardView
          card={card}
          followUpsEnabled={followUpsEnabled}
          onFollowUp={onFollowUp}
          presentation={presentation}
        />
      );
    case 'generic':
      return (
        <GenericInfoCardView
          card={card}
          followUpsEnabled={followUpsEnabled}
          onFollowUp={onFollowUp}
          presentation={presentation}
        />
      );
  }
}
